/**
 * Winziger IndexedDB-Key-Value-Store fürs Offline-Arbeiten. Ein einziger
 * Object-Store statt mehrerer (Base-Version, ausstehende Änderung, Zeit-
 * stempel sind einfach eigene Schlüssel-Präfixe) – IndexedDB kann Blobs
 * direkt speichern, kein Umweg über Base64 wie bei einem Text-only-Speicher
 * (localStorage/Capacitor Storage) nötig.
 */

const DB_NAME = "lernapp-offline";
const STORE = "kv";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = db.transaction(STORE, mode).objectStore(STORE);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export const offlineStore = {
  get<T>(key: string): Promise<T | undefined> {
    return tx("readonly", (s) => s.get(key));
  },
  set(key: string, value: unknown): Promise<void> {
    return tx("readwrite", (s) => s.put(value, key)).then(() => undefined);
  },
  remove(key: string): Promise<void> {
    return tx("readwrite", (s) => s.delete(key)).then(() => undefined);
  },
  /** Alle Schlüssel mit einem Präfix – für "gibt es offene Änderungen?". */
  async keysWithPrefix(prefix: string): Promise<string[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const out: string[] = [];
      const req = db.transaction(STORE, "readonly").objectStore(STORE).openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return resolve(out);
        if (String(cursor.key).startsWith(prefix)) out.push(String(cursor.key));
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
  },
};
